import React, { useEffect, useMemo, useState } from 'react';
import { Batiment, Personnel, Vehicule } from '../../../../shared/types/game';
import { getApiBaseUrl } from '../../../../core/config/api';
import { useVehicleCrewStatus } from '../hooks/useVehicleCrewStatus';
import { useStatusPalette } from '../hooks/useStatusPalette';

/**
 * Étape 4 — Tiroir Réseau.
 * Vue TRANSVERSE : tout le parc et tout le personnel, tous sites confondus. Elle
 * répond aux questions qu'une fiche de site ne peut pas traiter — « où est mon
 * VSAV ? », « qui est dans le groupe B ? » — avec recherche, filtre par site et tris.
 */

type Tab = 'parc' | 'personnel';
type SortKey = 'nom' | 'site' | 'type' | 'grade';

interface Props {
  playerVehicles: Vehicule[];
  playerPersonnel: Personnel[];
  buildings: Batiment[];
}

const ReseauView: React.FC<Props> = ({ playerVehicles, playerPersonnel, buildings }) => {
  const [tab, setTab] = useState<Tab>('parc');
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState<number | 'all'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('nom');

  const { vehicles: crewStatus } = useVehicleCrewStatus();
  const { palette } = useStatusPalette();

  /** Compétences effectives par agent (grade ∪ spécialités) — cf. /api/staff-competences. */
  const [competencesByStaff, setCompetencesByStaff] = useState<Record<number, string[]>>({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
        const res = await fetch(`${getApiBaseUrl(true)}/staff-competences`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        if (!alive || !res.ok || !data.success) return;
        setCompetencesByStaff(data.competencesByStaff || {});
      } catch {
        /* endpoint indisponible : la colonne reste vide, le reste de la vue fonctionne */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const competencesOf = (staffId: number): string[] => competencesByStaff[Number(staffId)] || [];

  const armableById = useMemo(() => {
    const m = new Map<number, boolean | null>();
    for (const c of crewStatus) m.set(Number(c.vehicleId), c.armable);
    return m;
  }, [crewStatus]);

  const siteName = useMemo(() => {
    const map = new Map<number, string>();
    for (const b of buildings || []) map.set(Number(b.id), b.nom || `Centre #${b.id}`);
    return (id: number | undefined) => (id != null && map.has(id) ? map.get(id)! : 'Hors centre');
  }, [buildings]);

  const siteOptions = useMemo(
    () =>
      (buildings || [])
        .map((b) => ({ id: Number(b.id), name: b.nom || `Centre #${b.id}` }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [buildings],
  );

  const q = search.trim().toLowerCase();

  const vehicleRows = useMemo(() => {
    const rows = (playerVehicles || [])
      .filter((v) => (siteFilter === 'all' ? true : Number(v.batiment_id) === siteFilter))
      .filter((v) =>
        q
          ? [v.nom, v.type, siteName(v.batiment_id)].some((x) => String(x || '').toLowerCase().includes(q))
          : true,
      );
    return rows.sort((a, b) => {
      if (sortKey === 'site') return siteName(a.batiment_id).localeCompare(siteName(b.batiment_id));
      if (sortKey === 'type') return String(a.type || '').localeCompare(String(b.type || ''));
      return String(a.nom || '').localeCompare(String(b.nom || ''));
    });
  }, [playerVehicles, siteFilter, q, sortKey, siteName]);

  const staffRows = useMemo(() => {
    const rows = (playerPersonnel || [])
      .filter((p) => (siteFilter === 'all' ? true : Number(p.batiment_id) === siteFilter))
      .filter((p) =>
        q
          ? [
              p.nom,
              p.prenom,
              p.grade,
              p.groupe,
              siteName(p.batiment_id),
              // Lecture directe de l'état : `competencesOf` changerait à chaque rendu
              // et invaliderait ce mémo en boucle.
              ...(competencesByStaff[Number(p.id)] || []),
            ].some((x) => String(x || '').toLowerCase().includes(q))
          : true,
      );
    return rows.sort((a, b) => {
      if (sortKey === 'site') return siteName(a.batiment_id).localeCompare(siteName(b.batiment_id));
      if (sortKey === 'grade') return (a.grade_ordre ?? 999) - (b.grade_ordre ?? 999);
      return `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`);
    });
  }, [playerPersonnel, siteFilter, q, sortKey, siteName, competencesByStaff]);

  const vehicleStatus = (v: Vehicule) => {
    if (v.statut === 'disponible') {
      return armableById.get(Number(v.id)) === false ? palette.materiel : palette.disponible;
    }
    if (v.statut === 'en_mission') return palette.en_mission;
    if (v.statut === 'en_maintenance') return palette.en_maintenance;
    return palette.panne;
  };

  const th = 'border-b border-slate-700/60 px-2 py-1.5 text-left text-[9px] font-semibold uppercase tracking-wide text-slate-500';
  const td = 'border-b border-slate-800/60 px-2 py-1.5 text-[11px] text-slate-200';

  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {(['parc', 'personnel'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                tab === t
                  ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-100'
                  : 'border-slate-600/50 bg-slate-800/40 text-slate-300 hover:text-white'
              }`}
            >
              {t === 'parc' ? `Parc (${playerVehicles.length})` : `Personnel (${playerPersonnel.length})`}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tab === 'parc' ? 'Chercher un engin, un type, un site…' : 'Chercher un agent, un grade, un site…'}
            className="w-56 rounded border border-slate-600/60 bg-slate-900/80 px-2 py-1 text-[11px] text-slate-100 placeholder:text-slate-500"
          />
          <select
            value={String(siteFilter)}
            onChange={(e) => setSiteFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="rounded border border-slate-600/60 bg-slate-900/80 px-2 py-1 text-[11px] text-slate-100"
          >
            <option value="all">Tous les sites</option>
            {siteOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="rounded border border-slate-600/60 bg-slate-900/80 px-2 py-1 text-[11px] text-slate-100"
          >
            <option value="nom">Tri : nom</option>
            <option value="site">Tri : site</option>
            {tab === 'parc' ? <option value="type">Tri : type</option> : <option value="grade">Tri : grade</option>}
          </select>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-700/60 bg-slate-950/40">
        {tab === 'parc' ? (
          <table className="w-full min-w-[620px]">
            <thead className="sticky top-0 bg-slate-950/90">
              <tr>
                <th className={th}>Engin</th>
                <th className={th}>Type</th>
                <th className={th}>Site</th>
                <th className={th}>État</th>
                <th className={th}>Km</th>
              </tr>
            </thead>
            <tbody>
              {vehicleRows.map((v) => {
                const st = vehicleStatus(v);
                return (
                  <tr key={v.id}>
                    <td className={`${td} font-semibold text-white`}>{v.nom}</td>
                    <td className={td}>{v.type}</td>
                    <td className={td}>{siteName(v.batiment_id)}</td>
                    <td className={td}>
                      <span
                        className="rounded px-1.5 py-0.5 text-[9px] font-semibold"
                        style={{ backgroundColor: st.bg, color: st.fg }}
                      >
                        {st.label}
                      </span>
                    </td>
                    <td className={td}>{Number(v.kilometrage || 0).toLocaleString('fr-FR')}</td>
                  </tr>
                );
              })}
              {vehicleRows.length === 0 ? (
                <tr>
                  <td className="px-2 py-6 text-center text-[11px] text-slate-500" colSpan={5}>
                    Aucun engin ne correspond.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        ) : (
          <table className="w-full min-w-[620px]">
            <thead className="sticky top-0 bg-slate-950/90">
              <tr>
                <th className={th}>Agent</th>
                <th className={th}>Grade</th>
                <th className={th}>Site</th>
                <th className={th}>Groupe</th>
                <th className={th}>Compétences</th>
                <th className={th}>Planning</th>
              </tr>
            </thead>
            <tbody>
              {staffRows.map((p) => (
                <tr key={p.id} className={p.actif ? '' : 'opacity-50'}>
                  <td className={`${td} font-semibold text-white`}>
                    {p.nom} {p.prenom}
                  </td>
                  <td className={td}>{p.grade}</td>
                  <td className={td}>{siteName(p.batiment_id)}</td>
                  <td className={td}>
                    {p.groupe ? (
                      <span className="rounded border border-slate-600/50 bg-slate-800/60 px-1.5 py-0.5 text-[9px] font-semibold">
                        {p.groupe}
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className={td}>
                    {competencesOf(p.id).length > 0 ? (
                      <span className="flex flex-wrap gap-0.5">
                        {competencesOf(p.id).map((c) => (
                          <span
                            key={c}
                            className="rounded border border-sky-500/40 bg-sky-950/30 px-1 text-[9px] text-sky-200"
                          >
                            {c}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-slate-600">—</span>
                    )}
                  </td>
                  <td className={td}>{p.planning_statut_nom || '—'}</td>
                </tr>
              ))}
              {staffRows.length === 0 ? (
                <tr>
                  <td className="px-2 py-6 text-center text-[11px] text-slate-500" colSpan={6}>
                    Aucun agent ne correspond.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ReseauView;
